/**
 * Chatbot configuration — the platform-wide on/off switch for Niva, the BL AI Deal Assistant.
 *
 * This used to be a pair of environment variables (CHATBOT_ENABLED on the API,
 * VITE_ENABLE_CHATBOT in the frontend bundle), which meant turning the
 * assistant off took a redeploy of both and was invisible from the product.
 * It now lives in the `system_config` table, owned from here.
 */
import { useEffect, useState } from "react";
import { Bot, Loader2, ShieldCheck } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface ChatbotConfig {
  enabled: boolean;
  updated_at: string | null;
}

export default function SuperAdminChatbotPage() {
  const [config, setConfig] = useState<ChatbotConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    superAdminApi
      .get<ChatbotConfig>("/super-admin/chatbot-config")
      .then((c) => { if (!stale) setConfig(c); })
      .catch((e) => { if (!stale) setError(e instanceof Error ? e.message : "Could not load the chatbot setting."); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, []);

  const toggle = async () => {
    if (!config || saving) return;
    const next = !config.enabled;
    setSaving(true);
    setError(null);
    try {
      const updated = await superAdminApi.patch<ChatbotConfig>("/super-admin/chatbot-config", { enabled: next });
      setConfig(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the change.");
    } finally {
      setSaving(false);
    }
  };

  const enabled = config?.enabled ?? false;

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Super Admin"
        icon={Bot}
        title="Chatbot"
        titleAccent="Config"
        description="The platform-wide switch for Niva, the BL AI Deal Assistant. It applies to every organization and takes effect immediately — no redeploy."
      />

      <section className="rounded-2xl border border-border/80 bg-card p-5 shadow-sm sm:p-6">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading the current setting…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-base font-bold text-foreground">Niva — BL AI Deal Assistant</div>
                  <p className="mt-0.5 max-w-xl text-sm text-muted-foreground">
                    {enabled
                      ? "On — signed-in members see Niva in the corner of every page and can search listings with her."
                      : "Off — her launcher is hidden and the chat endpoint answers 404 for everyone."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="Toggle Niva"
                onClick={toggle}
                disabled={saving || !config}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-60 ${
                  enabled ? "bg-primary" : "bg-muted-foreground/30"
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ${
                    enabled ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            {config?.updated_at && (
              <p className="mt-4 border-t border-border/60 pt-3 text-xs text-muted-foreground">
                Last changed {new Date(config.updated_at).toLocaleString()}
              </p>
            )}
          </>
        )}

        {error && (
          <p className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
            {error}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-border/70 bg-muted/30 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm leading-6 text-muted-foreground">
            Turning this off hides the bubble <em>and</em> makes the chat endpoint
            answer 404 — a stale browser tab cannot keep talking to the assistant.
            Subscription rules are unchanged: when it is on, free (Basic) buyers
            still get the upgrade prompt rather than a reply.
          </p>
        </div>
      </section>
    </main>
  );
}
